import { useMemo } from 'react';
import { Bubble } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import styles from '../../MetaReportViewer.module.css';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

/* ─── Theme helpers ───────────────────────────────────────────────────── */

const getThemeColors = () => {
  const root =
    typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  return {
    primaryText: root?.getPropertyValue('--tp')?.trim() || '#f3f4f6',
    secondaryText: root?.getPropertyValue('--ts2')?.trim() || '#d1d5db',
  };
};

/* ─── Waterfall ───────────────────────────────────────────────────────── */

const WATERFALL_STAGES = [
  { key: 'impressions',       label: 'Impressions',  color: '#3b82f6', bg: 'rgba(59,130,246,0.18)' },
  { key: 'clicks',            label: 'Clicks',       color: '#f97316', bg: 'rgba(249,115,22,0.18)' },
  { key: 'landing_page_views',label: 'Landing Page', color: '#22c55e', bg: 'rgba(34,197,94,0.18)'  },
  { key: 'add_to_cart',       label: 'Add to Cart',  color: '#eab308', bg: 'rgba(234,179,8,0.18)'  },
  { key: 'initiate_checkout', label: 'Checkout',     color: '#a855f7', bg: 'rgba(168,85,247,0.18)' },
  { key: 'conversions',       label: 'Purchase',     color: '#10b981', bg: 'rgba(16,185,129,0.18)' },
];

const getStageValue = (row, key) => {
  if (!row) return 0;
  switch (key) {
    case 'impressions':        return Number(row.impressions) || 0;
    case 'clicks':             return Number(row.link_clicks ?? row.clicks) || 0;
    case 'landing_page_views': return Number(row.landing_page_views) || 0;
    case 'add_to_cart':        return Number(row.add_to_cart) || 0;
    case 'initiate_checkout':  return Number(row.initiate_checkout) || 0;
    case 'conversions':        return Number(row.conversions ?? row.purchases) || 0;
    default:                   return Number(row[key]) || 0;
  }
};

const fmtCount = (n) =>
  Number.isFinite(n) ? n.toLocaleString('en-US') : '—';

const stepPct = (from, to) => {
  if (!from || from === 0) return null;
  return Math.min(100, (to / from) * 100);
};

export const LeakWaterfallChart = ({ row }) => {
  const stages = useMemo(() => {
    return WATERFALL_STAGES.map((stage) => ({
      ...stage,
      value: getStageValue(row, stage.key),
    }));
  }, [row]);

  const logMax = useMemo(() => {
    const max = Math.max(...stages.map((s) => s.value), 1);
    return Math.log(max + 1);
  }, [stages]);

  return (
    <div className={styles.matrixWaterfallCard}>
      <div className={styles.waterfallFunnel}>
        {stages.map((stage, i) => {
          const barHeightPct = logMax > 0
            ? (Math.log(stage.value + 1) / logMax) * 100
            : 0;
          const prev = stages[i - 1];
          const pct = i > 0 ? stepPct(prev.value, stage.value) : null;
          const drop = i > 0 ? stage.value - prev.value : null;
          const isGood = pct !== null && pct >= 50;

          return (
            <div key={stage.key} className={styles.waterfallStage}>
              {i > 0 && (
                <div className={styles.waterfallConnector}>
                  <span
                    className={styles.waterfallConnectorPct}
                    style={{ color: isGood ? '#22c55e' : '#f87171' }}
                  >
                    {pct !== null ? `${pct.toFixed(1)}%` : '—'}
                  </span>
                  <span className={styles.waterfallConnectorDrop}>
                    {drop !== null ? fmtCount(drop) : ''}
                  </span>
                </div>
              )}

              <div className={styles.waterfallBarColumn}>
                <span className={styles.waterfallBarValue}>
                  {fmtCount(stage.value)}
                </span>
                <div className={styles.waterfallBarTrack}>
                  <div
                    className={styles.waterfallBarFill}
                    style={{
                      height: `${barHeightPct}%`,
                      background: stage.bg,
                      borderTop: `2px solid ${stage.color}`,
                    }}
                  />
                </div>
                <span className={styles.waterfallBarLabel}>{stage.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Ads Bubble Matrix ───────────────────────────────────────────────── */

// Color by role_label — mirrors how adset matrix colors by audience_family_role
const getAdBubbleColor = (row) => {
  const role = String(row?.role_label || '').trim().toLowerCase();
  if (role === 'leading ad') return '#22c55ecc';
  if (role === 'supporting ad') return '#3b82f6cc';
  if (role === 'testing ad' || role === 'test') return '#f59e0bcc';
  if (role === 'underperformer' || role === 'weak') return '#ef4444cc';
  if (role === 'reactivation') return '#a855f7cc';
  if (role === 'mixed') return '#9ca3afcc';

  const commercial = String(row?.commercial_label || '').trim().toLowerCase();
  if (commercial === 'commercial winner') return '#22c55ecc';
  if (commercial === 'low performer' || commercial === 'drag') return '#ef4444cc';
  if (commercial === 'testing phase') return '#f59e0bcc';
  return '#9ca3afcc';
};

const normalizeAdMetric = (rows, accessor) => {
  const values = rows.map(accessor).map((v) => Number(v) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) return () => 50;
  return (row) => (((Number(accessor(row)) || 0) - min) / (max - min)) * 100;
};

const buildAdsBubbleData = (rows = []) => {
  if (!rows.length) return [];
  const spendTotal = rows.reduce((s, r) => s + (Number(r.spend) || 0), 0);
  const maxSpend = Math.max(...rows.map((r) => Number(r.spend) || 0));
  const roasScore = normalizeAdMetric(rows, (r) => r?.roas);
  const ctrScore = normalizeAdMetric(rows, (r) => r?.ctr);
  const lpvCvrScore = normalizeAdMetric(rows, (r) => r?.landing_page_view_conversion_rate);

  return rows.map((row, i) => {
    const spend = Number(row.spend) || 0;
    const spendShare = spendTotal > 0 ? (spend / spendTotal) * 100 : 0;
    const efficiency = (roasScore(row) * 0.5) + (ctrScore(row) * 0.25) + (lpvCvrScore(row) * 0.25);
    const spendRatio = maxSpend > 0 ? spend / maxSpend : 0;
    const radius = Math.max(10, Math.min(42, 10 + spendRatio * 32));
    return {
      x: Number(efficiency.toFixed(2)),
      y: Number(spendShare.toFixed(2)),
      r: Number(radius.toFixed(2)),
      label: row.ad_name || row.ad_id || `Ad ${i + 1}`,
      color: getAdBubbleColor(row),
      row,
    };
  });
};

const adsQuadrantPlugin = {
  id: 'adsQuadrantPlugin',
  beforeDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales?.x || !scales?.y) return;
    const { primaryText } = getThemeColors();
    const stroke = 'rgba(100, 149, 237, 0.5)';
    const x50 = scales.x.getPixelForValue(50);
    const y50 = scales.y.getPixelForValue(50);

    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(x50, chartArea.top);
    ctx.lineTo(x50, chartArea.bottom);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(chartArea.left, y50);
    ctx.lineTo(chartArea.right, y50);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.fillStyle = primaryText;
    ctx.font = '13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Spend Risk', chartArea.left + 15, chartArea.top + 25);
    ctx.fillText('Low-Impact Weak', chartArea.left + 15, chartArea.bottom - 12);
    ctx.textAlign = 'right';
    ctx.fillText('Scale Winners', chartArea.right - 15, chartArea.top + 25);
    ctx.fillText('Hidden Quality', chartArea.right - 15, chartArea.bottom - 12);
    ctx.restore();
  },
};

const adsBubbleLabelPlugin = {
  id: 'adsBubbleLabelPlugin',
  afterDraw(chart) {
    const { ctx, tooltip } = chart;
    const { primaryText } = getThemeColors();

    // Hide all bubble labels while the tooltip is open — prevents canvas text
    // from bleeding through the tooltip card regardless of z-order.
    if (tooltip && tooltip.opacity > 0) return;

    ctx.save();
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    chart.data.datasets.forEach((dataset, di) => {
      chart.getDatasetMeta(di).data.forEach((point, pi) => {
        const raw = dataset.data?.[pi] || {};
        const label = raw?.row?.role_label || dataset.label;
        ctx.fillStyle = primaryText;
        ctx.fillText(label, point.x, point.y);
      });
    });

    ctx.restore();
  },
};

export const AdsBubbleMatrix = ({ rows = [], selectedAdKey, onSelect }) => {
  const points = useMemo(() => buildAdsBubbleData(rows), [rows]);
  const { primaryText: titleColor, secondaryText: tickColor } = getThemeColors();

  const chartData = {
    datasets: points.map((point) => {
      const key = point.row.ad_id || point.row.ad_name;
      const isSelected = key === selectedAdKey;
      return {
        label: point.label,
        data: [point],
        backgroundColor: point.color,
        borderColor: isSelected ? '#ffffff' : 'transparent',
        borderWidth: isSelected ? 3 : 0,
        radius: (ctx) => ctx.raw?.r || 10,
        hoverBackgroundColor: point.color.replace('cc', ''),
        hoverBorderWidth: 3,
        hoverBorderColor: '#ffffff',
        hoverRadius: 2,
        hitRadius: 14,
        pointStyle: 'circle',
        clip: false,
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    normalized: true,
    interaction: { mode: 'nearest', intersect: true },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: '4) Ad Efficiency Matrix',
        color: titleColor,
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        backgroundColor: '#111827',
        borderColor: '#374151',
        borderWidth: 1,
        titleColor: '#f9fafb',
        bodyColor: '#f9fafb',
        displayColors: false,
        padding: 12,
        cornerRadius: 10,
        caretPadding: 8,
        xAlign: 'center',
        yAlign: 'top',
        callbacks: {
          title: (ctx) => ctx?.[0]?.raw?.row?.ad_name || ctx?.[0]?.dataset?.label || 'Ad',
          label: (ctx) => {
            const { row } = ctx.raw || {};
            return [
              `Role: ${row?.role_label || '—'}`,
              `Type: ${row?.creative_type || '—'}`,
              `ROAS: ${row?.roas != null ? `${row.roas}×` : '—'}`,
              `CTR: ${row?.ctr != null ? `${row.ctr}%` : '—'}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Efficiency Quality', color: titleColor, font: { size: 14, weight: 'bold' } },
        grid: { display: false },
        ticks: { color: tickColor, stepSize: 20 },
      },
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Scale / Spend Share', color: titleColor, font: { size: 14, weight: 'bold' } },
        grid: { display: false },
        ticks: { color: tickColor, stepSize: 20, callback: (v) => `${v}` },
      },
    },
    layout: { padding: 20 },
    onClick: (_, elements) => {
      if (!elements?.length || typeof onSelect !== 'function') return;
      const { datasetIndex, index } = elements[0];
      const clicked = chartData.datasets[datasetIndex]?.data?.[index];
      if (clicked?.row) {
        onSelect(clicked.row.ad_id || clicked.row.ad_name || null);
      }
    },
  };

  if (!points.length) return null;

  return (
    <div className={styles.matrixChartCard}>
      <div className={styles.matrixChartWrap}>
        <Bubble
          data={chartData}
          options={chartOptions}
          plugins={[adsQuadrantPlugin, adsBubbleLabelPlugin]}
        />
      </div>
    </div>
  );
};
